/**
 * shared/hooks/marcaTicket.ts — la marca del ticket vive en el SERVIDOR, por empresa.
 *
 * Tres dueños distintos para la configuración del ticket:
 *   · MARCA (logo, lema, redes, textos, diseño, campos) → businesses.config.ticket. La ven todas las
 *     cajas de la empresa. Nada más en la app escribe businesses.config, así que no hay quien la pise.
 *     Permiso: el de la tabla (dueño, o administrador/director de esa empresa).
 *   · CONTACTO de la tienda (dirección, teléfono, WhatsApp) → columnas de stores, que ya se editan en
 *     Tiendas. Aquí sólo se leen.
 *   · EQUIPO (impresión automática, copias, ancho viejo) → sólo esta computadora.
 *
 * Copia local (localStorage) para imprimir sin esperar a la red y sin internet. Mientras alguien edita,
 * la copia queda «pendiente» y ninguna sincronización la pisa hasta que se publique o descarte.
 */
import { supabase } from '@lib/supabase/client'
import type { TicketConfig } from './useTicketConfig'

const LS_MARCA = 'celmaxx_ticket_marca'
export const EVENTO_MARCA = 'celmaxx:marca'

export const CLAVES_EQUIPO = [
  'autoPrint',
  'printClientCopy',
  'printStoreCopy',
  'printCorteOnClose',
  'printCorteEnabled',
  'ticketWidth',
] as const
export const CLAVES_CONTACTO = ['storePhone', 'storeWhatsApp', 'storeAddress'] as const

export interface ContactoTienda {
  telefono: string | null
  direccion: string | null
  whatsapp: string | null
}

export interface CacheMarca {
  businessId: string | null
  storeId: string | null
  /** Marca publicada (o en edición si `pendiente`). null = la empresa aún no publica: vale la local. */
  marca: Partial<TicketConfig> | null
  tienda: ContactoTienda | null
  pendiente: boolean
}

/** Sólo lo que es de la empresa: sin claves del equipo ni el contacto de la tienda. */
export function soloMarca(cfg: Partial<TicketConfig>): Partial<TicketConfig> {
  const fuera = new Set<string>([...CLAVES_EQUIPO, ...CLAVES_CONTACTO])
  return Object.fromEntries(
    Object.entries(cfg).filter(([k]) => !fuera.has(k))
  ) as Partial<TicketConfig>
}

export function leerCacheMarca(): CacheMarca | null {
  try {
    const raw = localStorage.getItem(LS_MARCA)
    return raw ? (JSON.parse(raw) as CacheMarca) : null
  } catch {
    return null
  }
}

export function escribirCacheMarca(c: CacheMarca): void {
  try {
    localStorage.setItem(LS_MARCA, JSON.stringify(c))
  } catch {
    /* sin storage: se usa lo local */
  }
  try {
    window.dispatchEvent(new Event(EVENTO_MARCA))
  } catch {
    /* fuera del navegador */
  }
}

/** Configuración efectiva: local ← marca de la empresa ← contacto de la tienda (si lo tiene). */
export function aplicarMarca(base: TicketConfig, c: CacheMarca | null): TicketConfig {
  const out = { ...base, ...(c?.marca ? soloMarca(c.marca) : {}) }
  const t = c?.tienda
  if (t?.direccion) out.storeAddress = t.direccion
  if (t?.telefono) out.storePhone = t.telefono
  if (t?.whatsapp) out.storeWhatsApp = t.whatsapp
  return out
}

/** Una edición de marca queda en la copia local como pendiente, sin tocar el servidor. */
export function marcarEdicion(cfg: TicketConfig): void {
  const c = leerCacheMarca()
  escribirCacheMarca({
    businessId: c?.businessId ?? null,
    storeId: c?.storeId ?? null,
    tienda: c?.tienda ?? null,
    marca: soloMarca(cfg),
    pendiente: true,
  })
}

/**
 * Trae la marca de la empresa y el contacto de la tienda activas. Sin red no borra nada. Una edición
 * pendiente de la MISMA empresa no se pisa: la manda quien la está editando.
 */
export async function sincronizarMarca(businessId: string, storeId: string | null): Promise<void> {
  const [b, s] = await Promise.all([
    supabase.from('businesses').select('config').eq('id', businessId).maybeSingle(),
    storeId
      ? supabase.from('stores').select('phone, address, wa_number').eq('id', storeId).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
  ])
  if (b.error) return
  const prev = leerCacheMarca()
  const pendiente = prev?.businessId === businessId && prev.pendiente
  const servidor = (b.data?.config as { ticket?: Partial<TicketConfig> } | null)?.ticket ?? null
  const st = s.data as {
    phone: string | null
    address: string | null
    wa_number: string | null
  } | null
  escribirCacheMarca({
    businessId,
    storeId,
    marca: pendiente ? prev!.marca : servidor ? soloMarca(servidor) : null,
    pendiente,
    tienda: st
      ? { telefono: st.phone, direccion: st.address, whatsapp: st.wa_number }
      : s.error
        ? (prev?.tienda ?? null)
        : null,
  })
}

/**
 * Publica la marca a la empresa: todas sus cajas la reciben. Lee la config FRESCA y cambia sólo la
 * llave `ticket` (no pisa el resto). Si la regla de la tabla no deja actualizar, no hay fila de
 * regreso: se avisa, no se finge éxito.
 * ponytail: dos personas publicando a la vez → gana la última; bastaría un RPC con jsonb_set.
 */
export async function publicarMarca(
  businessId: string,
  cfg: TicketConfig
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { data: fila, error } = await supabase
    .from('businesses')
    .select('config')
    .eq('id', businessId)
    .maybeSingle()
  if (error) return { ok: false, error: 'No se pudo leer la empresa. Revisa la conexión.' }
  const marca = soloMarca(cfg)
  const config = { ...((fila?.config as Record<string, unknown>) ?? {}), ticket: marca }
  const { data, error: e } = await supabase
    .from('businesses')
    .update({ config })
    .eq('id', businessId)
    .select('id')
  if (e) return { ok: false, error: 'No se pudo guardar. Revisa la conexión.' }
  if (!data?.length)
    return {
      ok: false,
      error:
        'No tienes permiso para cambiar la marca de esta empresa. Lo hace el dueño, un administrador o un director.',
    }
  const c = leerCacheMarca()
  escribirCacheMarca({
    businessId,
    storeId: c?.storeId ?? null,
    tienda: c?.tienda ?? null,
    marca,
    pendiente: false,
  })
  return { ok: true }
}
